import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, EMPTY, filter, finalize, forkJoin, Observable, tap } from 'rxjs';
import { formatDistanceToNow } from 'date-fns';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { UserService } from '@services/user/user.service';
import { NetworkMigrationService } from '@services/network-migration.service';
import { Building } from '@app/shared/models/building.interface';
import {
  NETWORK_MIGRATION_STATUS_LABELS,
  NetworkMigrationStatus,
  NetworkMigrationTableRow,
  NetworkMigrationWorkflow
} from '@app/shared/models/network-migration.model';
import {
  StartMigrationDialog
} from '@app/global-administration/network-migration/start-migration-dialog/start-migration-dialog';
import { NotificationBlockComponent, StatusClass } from '@components/notification-block/notification-block.component';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import { ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';

@Component({
  selector: 'app-network-migration',
  imports: [
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatTableModule,
    NotificationBlockComponent
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './network-migration.component.html',
  styleUrl: './network-migration.component.scss'
})
export class NetworkMigrationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly api = inject(NetworkMigrationService);
  private readonly dialog = inject(MatDialog);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  readonly loading = signal(true);
  readonly contextError = signal('');
  readonly startingMigration = signal(false);

  readonly buildings = signal<Building[]>([]);
  readonly displayedColumns = [
    'status',
    'buildingName',
    'buildingId',
    'customer',
    'nodes',
    'progress',
    'lastUpdated',
    'action'
  ];
  readonly rows = signal<NetworkMigrationTableRow[]>([]);
  readonly searchQuery = signal('');
  readonly requiresAttentionOnly = signal(false);
  readonly filteredRows = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const requiresAttentionOnly = this.requiresAttentionOnly();

    return this.rows().filter((row) => {
      if (requiresAttentionOnly && !row.requiresAttention) {
        return false;
      }

      if (!query) {
        return true;
      }

      return [row.buildingName, row.buildingId, row.customer].some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(query)
      );
    });
  });
  readonly resultsLabel = computed(() => `Showing ${this.filteredRows().length} of ${this.rows().length} buildings`);
  readonly companyNamesById = signal<ReadonlyMap<number, string>>(new Map());

  ngOnInit(): void {
    this.loadContext();
  }

  updateSearchQuery(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchQuery.set(target?.value ?? '');
  }

  updateRequiresAttentionFilter(event: MatSlideToggleChange): void {
    this.requiresAttentionOnly.set(event.checked);
  }

  openMigrationWorkflow(row: NetworkMigrationTableRow): void {
    void this.router.navigate(['/global-administration', 'network-migration', 'workflow', row.buildingId, row.id], {
      queryParams: {
        workflowBuildingName: row.buildingName,
        workflowCustomer: row.customer
      }
    });
  }

  startNewMigration(): void {
    this.contextError.set('');
    this.pickBuildingToMigrate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedBuilding) => this.confirmStartMigration(selectedBuilding));
  }

  private getMigratableBuildings(): Building[] {
    const migratedBuildingIds = new Set(this.rows().map((row) => row.buildingId));
    return this.buildings().filter((building) => !migratedBuildingIds.has(building.id));
  }

  private pickBuildingToMigrate(): Observable<Building> {
    return this.dialog
      .open(StartMigrationDialog, {
        width: '420px',
        data: { buildings: this.getMigratableBuildings() }
      })
      .afterClosed()
      .pipe(filter((selectedBuilding): selectedBuilding is Building => Boolean(selectedBuilding)));
  }

  private confirmStartMigration(building: Building): void{
    this.confirmationDialogService
      .open(
        new ConfirmDialogData(`Are you sure you want to initiate transfer for ${building.name}?`,
          'Start New Transfer')
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.startTransferAndNavigate(building)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }
      });
  }

  private startTransferAndNavigate(building: Building): Observable<NetworkMigrationWorkflow> {
    this.startingMigration.set(true);

    return this.api.startNewTransfer(building.id).pipe(
      tap((workflow) => this.handleTransferStarted(building, workflow)),
      catchError(() => {
        this.showStartMigrationError(building);
        return EMPTY;
      }),
      finalize(() => this.startingMigration.set(false))
    );
  }

  private handleTransferStarted(building: Building, workflow: NetworkMigrationWorkflow): void {
    if (!Number.isSafeInteger(workflow.workflowId) || workflow.workflowId <= 0) {
      this.showStartMigrationError(building);
      return;
    }

    void this.router.navigate(
      ['/global-administration', 'network-migration', 'workflow', building.id, workflow.workflowId],
      {
        queryParams: {
          workflowBuildingName: building.name,
          workflowCustomer: this.companyNamesById().get(building.managingCompanyId)
        }
      }
    );
  }

  private showStartMigrationError(building: Building): void {
    const message =  `Unable to start migration for ${building.name}`;
    this.confirmationDialogService.error({ message, showConfirm: false });
  }

  loadContext(): void {
    this.loading.set(true);
    this.contextError.set('');

    forkJoin({
      workflows: this.api.getAllMigrationTransfer(),
      buildings: this.userService.getBuildings(),
      managingCompanies: this.api.getManagingCompanies()
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ workflows, buildings, managingCompanies }) => {
          this.buildings.set(buildings);
          const companyNamesById = new Map(managingCompanies.map((company) => [company.id, company.companyName]));
          this.companyNamesById.set(companyNamesById);

          const buildingsById = new Map(buildings.map((building) => [building.id, building]));
          this.rows.set(workflows.map((workflow) => this.mapWorkflowToRow(workflow, buildingsById, companyNamesById)));
          this.loading.set(false);
        },
        error: () => {
          this.contextError.set('Error occurred while loading workflows.');
          this.loading.set(false);
        }
    });
  }

  private mapWorkflowToRow(
    workflow: NetworkMigrationWorkflow,
    buildingsById: Map<number, Building>,
    companyNamesById: Map<number, string>
  ): NetworkMigrationTableRow {
    const building = buildingsById.get(workflow.buildingId);
    const updatedAt = new Date(workflow.updatedAt);

    return {
      id: workflow.workflowId,
      status: workflow.status,
      statusLabel: NETWORK_MIGRATION_STATUS_LABELS[workflow.status],
      statusClass: workflow.status.toLowerCase(),
      buildingName: building?.name ?? `Building #${workflow.buildingId}`,
      buildingId: workflow.buildingId,
      customer:
        building?.managingCompanyId === undefined ? null : companyNamesById.get(building.managingCompanyId) ?? null,
      totalNodes: workflow.totalNodes ?? null,
      completedNodes: workflow.completedNodes ?? null,
      progressPercent:
        Number.isFinite(workflow.totalNodes) && workflow.totalNodes > 0 && Number.isFinite(workflow.completedNodes)
          ? Math.min(100, Math.max(0, Math.round((workflow.completedNodes / workflow.totalNodes) * 100)))
          : null,
      lastUpdatedLabel: Number.isNaN(updatedAt.getTime())
        ? '—'
        : `${formatDistanceToNow(updatedAt, { addSuffix: true })}`,
      requiresAttention: workflow.status === 'FAILED'
    };
  }

  protected readonly StatusClass = StatusClass;
}
